import { createContext, useContext } from "react";
import type { ReactNode } from "react";
import type { CurrentPlan } from "@circle-react/types";
import { useBillingDetailsApi } from "../../hooks/useBillingDetailsApi";

interface BillingContextValue {
  currentPlan?: CurrentPlan;
  errorFetchingCurrentPlan: unknown;
  isCurrentPlanLoading: boolean;
  refetchCurrentPlan: () => unknown;
  showAddons?: boolean;
  showBillingDetails?: boolean;
  showBillingPortal?: boolean;
}

export const BillingContext = createContext<BillingContextValue>({
  currentPlan: undefined,
  showAddons: false,
  showBillingDetails: false,
  showBillingPortal: false,
  isCurrentPlanLoading: false,
  errorFetchingCurrentPlan: null,
  refetchCurrentPlan: () => {},
});

BillingContext.displayName = "BillingContext";

interface BillingProviderProps {
  children: ReactNode;
  showAddons?: boolean;
  showBillingDetails?: boolean;
  showBillingPortal?: boolean;
}

export const BillingProvider = ({
  showAddons,
  showBillingDetails,
  showBillingPortal,
  children,
}: BillingProviderProps) => {
  const {
    currentPlan,
    isCurrentPlanLoading,
    errorFetchingCurrentPlan,
    refetchCurrentPlan,
  } = useBillingDetailsApi();

  return (
    <BillingContext.Provider
      value={{
        currentPlan,
        showAddons,
        showBillingDetails,
        showBillingPortal,
        isCurrentPlanLoading,
        errorFetchingCurrentPlan,
        refetchCurrentPlan,
      }}
    >
      {children}
    </BillingContext.Provider>
  );
};

export const useBillingContext = () => useContext(BillingContext);
